//-- Copyright (c) 2021 TMT Insights Group, LLC
import styled from '@emotion/styled';
import { Grid, darken, lighten } from '@mui/material';
import { Highlighted } from '../DeliverablesV2';
import { selectAudioLanguages } from '@/redux/selectors/constantsSelectors';
import SimpleLabelValue from '@/components/_common/SimpleLabelValue';
import { useAppSelector } from '@/redux/hooks';

interface DeliverableTrackTableProps {
  columns: string[];
  color: string;
  data: any[];
}

type DeliverableCols = {
  label: string;
  key: string;
  col?: number;
};

type LineProps = {
  columns: string[];
} & (HeaderLine & DataLine);
type HeaderLine = { header: true };
type DataLine = { data: any; index: number; color: string };

const DeliverableTrackTable = ({ columns, color, data }: DeliverableTrackTableProps) => {
  return (
    <Wrapper container>
      {data.length > 0 ? (
        <>
          <Line columns={columns} header />
          {data.map((track, i) => (
            <Line key={`track-${i}`} columns={columns} data={track} index={i} color={color} />
          ))}
        </>
      ) : (
        <NoTracksMessage>No tracks to show</NoTracksMessage>
      )}
    </Wrapper>
  );
};

export default DeliverableTrackTable;

const Line = ({ columns, ...props }: LineProps) => {
  const AudioLanguages = useAppSelector(selectAudioLanguages);
  const colSettings: { [key: string]: DeliverableCols } = {
    track: {
      label: 'Track#',
      key: 'track_num',
      col: 2
    },
    type: {
      label: 'Type',
      key: 'type',
      col: 2
    },
    encoding: {
      label: 'Encoding',
      key: 'encoding',
      col: 3
    },
    language: {
      label: 'Language',
      key: 'language'
    }
  };

  const colsData = columns.map((col) => colSettings[col as keyof typeof colSettings]);
  const currColWidths = colsData.filter((col) => col.col).reduce((acc, col) => acc + (col.col as number), 0);
  const maxColWidths = colsData.filter((col) => !col.col).length;

  const renderValue = (key: string) => {
    const { data, index } = props as DataLine;
    const lang = key === 'language' ? AudioLanguages?.find((lang) => [data[key], data[key].replace('-', '_')].includes(lang.code)) : undefined;
    return key === 'track_num'
      ? (data[key] || index + 1).toString().padStart(2, '0')
      : key === 'language'
      ? SimpleLabelValue({ label: lang?.label, value: lang?.code, fallback: data[key] || '-' })
      : data[key] || '-';
  };

  return (
    <Track container header={+(!!props.header)} color={(props as DataLine).color}>
      {colsData.map((column, i) => {
        return (
          <Column
            header={+(!!props.header)}
            highlighted={+(column.key === 'language')}
            bg={(props as DataLine).color}
            key={`column-${i + 1}`}
            item
            xs={!column.col ? (12 - currColWidths) / maxColWidths : column.col}
          >
            {!props.header && column.key === 'language' ? (
              <Highlighted color={(props as DataLine).color}>{renderValue(column.key)}</Highlighted>
            ) : (
              <div>{props.header ? column.label : renderValue(column.key)}</div>
            )}
          </Column>
        );
      })}
    </Track>
  );
};

const Wrapper = styled(Grid)`
  row-gap: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Track = styled(Grid)<{ header: number; color: string }>`
  height: fit-content;
  background: ${({ header, color }) => (header ? 'transparent' : color + '3a')};
  align-items: ${({ header }) => (header ? 'flex-end' : 'center')};
  padding: ${({ theme }) => theme.spacing(1, 1.5)};
  opacity: ${({ header }) => (header ? 0.5 : 1)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border: ${({ theme, header, color }) =>
    header ? 'none' : `1px solid ${theme.palette.mode === 'light' ? lighten(color, 0.6) : darken(color, 0.6)}`};
  color: ${({ theme }) => (theme.palette.mode === 'light' ? theme.palette.text.primary : theme.palette.text.secondary)};
`;

const Column = styled(Grid)<{ header: number; highlighted: number; bg: string }>`
  > div {
    font-weight: ${({ theme, header }) => (header ? theme.typography.fontWeightBold : theme.typography.fontWeightNormal)};
    padding: ${({ theme }) => theme.spacing(0.5, 1)};
  }
`;

const NoTracksMessage = styled.h3`
  width: 100%;
  font-style: italic;
  opacity: 0.5;
  text-align: center;
  margin: 0;
  margin-top: ${({ theme }) => theme.spacing(2)};
  font-weight: ${({ theme }) => theme.typography.fontWeightNormal};
`;
